import { ISO_CODE_TO_ENGLISH_NAME } from "./countryMapping";
import {
  FALLBACK_COUNTRY_ALIASES,
  normalizeCountryAliasText,
} from "./countryAliases";

const unique = (values) => [...new Set(values.filter(Boolean))];

const countrySuggestionKey = (name, isoCode) =>
  isoCode
    ? `iso:${isoCode}`
    : `name:${normalizeCountryAliasText(name)}`;

const uniqueLabels = (rows) => {
  const seen = new Set();
  return rows
    .map((row) => row.label)
    .filter((label) => {
      const key = normalizeCountryAliasText(label);
      if (!label || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const PREFECTURE_SEARCH_KEYWORDS = {
  北海道: ["ほっかいどう", "hokkaido"],
  青森県: ["あおもり", "あおもりけん", "aomori"],
  岩手県: ["いわて", "いわてけん", "iwate"],
  宮城県: ["みやぎ", "みやぎけん", "miyagi"],
  秋田県: ["あきた", "あきたけん", "akita"],
  山形県: ["やまがた", "やまがたけん", "yamagata"],
  福島県: ["ふくしま", "ふくしまけん", "fukushima"],
  茨城県: ["いばらき", "いばらきけん", "ibaraki"],
  栃木県: ["とちぎ", "とちぎけん", "tochigi"],
  群馬県: ["ぐんま", "ぐんまけん", "gunma"],
  埼玉県: ["さいたま", "さいたまけん", "saitama"],
  千葉県: ["ちば", "ちばけん", "chiba"],
  東京都: ["とうきょう", "とうきょうと", "tokyo"],
  神奈川県: ["かながわ", "かながわけん", "kanagawa"],
  新潟県: ["にいがた", "にいがたけん", "niigata"],
  富山県: ["とやま", "とやまけん", "toyama"],
  石川県: ["いしかわ", "いしかわけん", "ishikawa"],
  福井県: ["ふくい", "ふくいけん", "fukui"],
  山梨県: ["やまなし", "やまなしけん", "yamanashi"],
  長野県: ["ながの", "ながのけん", "nagano"],
  岐阜県: ["ぎふ", "ぎふけん", "gifu"],
  静岡県: ["しずおか", "しずおかけん", "shizuoka"],
  愛知県: ["あいち", "あいちけん", "aichi"],
  三重県: ["みえ", "みえけん", "mie"],
  滋賀県: ["しが", "しがけん", "shiga"],
  京都府: ["きょうと", "きょうとふ", "kyoto"],
  大阪府: ["おおさか", "おおさかふ", "osaka"],
  兵庫県: ["ひょうご", "ひょうごけん", "hyogo"],
  奈良県: ["なら", "ならけん", "nara"],
  和歌山県: ["わかやま", "わかやまけん", "wakayama"],
  鳥取県: ["とっとり", "とっとりけん", "tottori"],
  島根県: ["しまね", "しまねけん", "shimane"],
  岡山県: ["おかやま", "おかやまけん", "okayama"],
  広島県: ["ひろしま", "ひろしまけん", "hiroshima"],
  山口県: ["やまぐち", "やまぐちけん", "yamaguchi"],
  徳島県: ["とくしま", "とくしまけん", "tokushima"],
  香川県: ["かがわ", "かがわけん", "kagawa"],
  愛媛県: ["えひめ", "えひめけん", "ehime"],
  高知県: ["こうち", "こうちけん", "kochi"],
  福岡県: ["ふくおか", "ふくおかけん", "fukuoka"],
  佐賀県: ["さが", "さがけん", "saga"],
  長崎県: ["ながさき", "ながさきけん", "nagasaki"],
  熊本県: ["くまもと", "くまもとけん", "kumamoto"],
  大分県: ["おおいた", "おおいたけん", "oita"],
  宮崎県: ["みやざき", "みやざきけん", "miyazaki"],
  鹿児島県: ["かごしま", "かごしまけん", "kagoshima"],
  沖縄県: ["おきなわ", "おきなわけん", "okinawa"],
};

export const filterDestinationSuggestions = ({
  inputValue,
  destinationType,
  prefectureNames,
  countryRows,
  countryAliasRows,
  limit = 20,
}) => {
  const query = normalizeCountryAliasText(inputValue);
  if (!query) return [];

  if (destinationType === "domestic") {
    return unique(prefectureNames)
      .filter((name) => {
        const keywords = [
          name,
          name.replace(/[都道府県]$/, ""),
          ...(PREFECTURE_SEARCH_KEYWORDS[name] || []),
        ];
        return keywords.some((keyword) =>
          normalizeCountryAliasText(keyword).includes(query),
        );
      })
      .slice(0, limit);
  }

  const aliasRows = [...FALLBACK_COUNTRY_ALIASES, ...(countryAliasRows || [])];
  const rowsByCountry = new Map();

  const mergeCountrySuggestion = ({ label, isoCode, keywords }) => {
    if (!label) return;
    const key = countrySuggestionKey(label, isoCode);
    const current = rowsByCountry.get(key) || {
      label,
      keywords: [],
    };
    current.keywords = unique([...current.keywords, ...keywords]);
    rowsByCountry.set(key, current);
  };

  (countryRows || []).forEach((country) => {
    const name = country.COUNTRY_NMV;
    if (!name) return;
    mergeCountrySuggestion({
      label: name,
      isoCode: country.ISO_CODE,
      keywords: unique([
        name,
        country.ISO_CODE,
        country.ISO_CODE ? ISO_CODE_TO_ENGLISH_NAME[country.ISO_CODE] : null,
      ]),
    });
  });

  aliasRows.forEach((alias) => {
    const label = alias.COUNTRY_NMV;
    if (!label) return;
    mergeCountrySuggestion({
      label,
      isoCode: alias.ISO_CODE,
      keywords: [
        label,
        alias.ALIAS_NMV,
        alias.ISO_CODE,
        alias.ISO_CODE ? ISO_CODE_TO_ENGLISH_NAME[alias.ISO_CODE] : null,
      ],
    });
  });

  const matchingRows = [...rowsByCountry.values()].filter((row) =>
      row.keywords.some((keyword) =>
        normalizeCountryAliasText(keyword).includes(query),
      ),
    );

  return uniqueLabels(matchingRows).slice(0, limit);
};
